// src/lib/dates.js
// Shared date-parsing helper. Pure, no React, no Supabase.

/**
 * Parse a date string as local time, not UTC.
 *
 * Date-only strings (e.g. "2026-04-01", from a DATE column) get parsed by
 * `new Date()` as UTC midnight. In any timezone behind UTC, that shifts the
 * calendar date backward once .getMonth()/.getDate() read it locally —
 * "2026-04-01" silently becomes "2026-03-31". Appending a bare local time
 * forces local parsing instead.
 *
 * Full timestamps (already containing "T", e.g. created_at, clock_in) are
 * left untouched — those represent a real moment in time, not a
 * calendar-date label, so UTC parsing is correct for them.
 */
export function parseLocalDate(dateStr) {
  return new Date(dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00`)
}

/**
 * Shared display formatter — closes TD-016 (Estimates.js/Invoices.js/Proposals.js
 * each independently reformatted dates, several inconsistently: some via
 * parseLocalDate, some via plain `new Date()` which carries the exact UTC-vs-local
 * bug TD-015 fixed elsewhere (Schedule.js, pnl.js) but never closed out in
 * Invoices.js/Dashboard.js/Transactions.js. Routing everything through
 * parseLocalDate here fixes that as a side effect, not a separate pass.
 *
 * style: 'short' (default) → "Jul 12, 2026", the common list-row format.
 *        'long' → "July 12, 2026", used for document-style headers (proposal/
 *        estimate expiry dates).
 */
export function formatDate(dateStr, style = 'short') {
  if (!dateStr) return '—'
  return parseLocalDate(dateStr).toLocaleDateString('en-US', style === 'long'
    ? { month: 'long', day: 'numeric', year: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * Serialize a Date object back to a local "YYYY-MM-DD" string — the inverse of
 * parseLocalDate. `date.toISOString().split('T')[0]` is the tempting shortcut
 * here and it's wrong: toISOString() converts to UTC first, so anything after
 * ~6-7pm local (US timezones) serializes as tomorrow's date. Read the local
 * calendar fields directly instead.
 */
export function toDateStr(date) {
  const y = date.getFullYear(), m = String(date.getMonth() + 1).padStart(2, '0'), d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}
